import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faUsers, faUser, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useFormatCurrency } from '../../hooks/useFormatCurrency';
import { DEFAULT_SHARE_RATIO, formatShareRange, getShareRatio } from '../../utils/helpers';
import { ShareRatios } from '../../types';

interface ParticipantPickerProps {
    participants: { [userId: string]: string };
    selected: string[];
    onChange: (next: string[]) => void;
    /** Enables the "only me" shortcut — pass the id the current user picked for this event. */
    currentUser?: string;
    /** Total in VND. When > 0 the picker previews the share each selected person owes. */
    amount?: number;
    /** The event's share ratios. Anyone off 100% shows it on their chip, and the
        preview becomes a range — one figure would be wrong for somebody. */
    shareRatios?: ShareRatios;
    /** Accent of the selected chips: expenses are indigo, sponsorships green. */
    tone?: 'accent' | 'green';
    disabled?: boolean;
    /** Names the group for screen readers — usually the same text as the field's label. */
    ariaLabel?: string;
}

/**
 * Picks who an amount is shared with, as a wrap of tappable chips.
 *
 * Chips beat the checkbox grid this replaced on both counts the picker is judged
 * by: the whole chip is the hit target rather than a 16px box, and a wrap packs
 * short Vietnamese names three to a row where a fixed 2-column grid left half of
 * every row empty. The running "N/M · each" strip answers the question the old
 * list could not — how much unticking someone actually moves the split.
 *
 * A chip carries the name and nothing else. An avatar disc sat here briefly and
 * read as clutter: two circles in one pill, the avatar and the tick, with only
 * the tick meaning anything.
 */
const ParticipantPicker: React.FC<ParticipantPickerProps> = ({
                                                                 participants,
                                                                 selected,
                                                                 onChange,
                                                                 currentUser,
                                                                 amount = 0,
                                                                 shareRatios,
                                                                 tone = 'accent',
                                                                 disabled = false,
                                                                 ariaLabel
                                                             }) => {
    const { t } = useTranslation();
    const { formatCurrency } = useFormatCurrency();

    const participantIds = useMemo(() => Object.keys(participants), [participants]);
    const allSelected = selected.length === participantIds.length && participantIds.length > 0;
    const onlyMeSelected = !!currentUser && selected.length === 1 && selected[0] === currentUser;
    const canPickOnlyMe = !!currentUser && participantIds.includes(currentUser);

    const perPerson = selected.length > 0 && amount > 0
        ? formatShareRange(amount, selected, shareRatios, formatCurrency)
        : '';

    const toggle = (participantId: string) => {
        onChange(
            selected.includes(participantId)
                ? selected.filter(id => id !== participantId)
                : [...selected, participantId]
        );
    };

    return (
        <div className="participant-picker" data-tone={tone}>
            {/* Summary — the count on the left, what it costs each person on the right */}
            <div className="flex items-center justify-between gap-2 mb-2 text-xs">
                <span className={`font-medium ${
                    selected.length === 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-600 dark:text-gray-400'
                }`}>
                    {selected.length === 0
                        ? t('shared.participantPicker.pickAtLeastOne')
                        : t('shared.participantPicker.countSelected', {
                            selected: selected.length,
                            total: participantIds.length
                        })
                    }
                </span>
                {perPerson && (
                    <span className="participant-picker-share" aria-live="polite">
                        {t('shared.participantPicker.perPerson', { amount: perPerson })}
                    </span>
                )}
            </div>

            {/* Shortcuts for the two splits that need no per-person thought at all */}
            <div className="flex flex-wrap gap-1.5 mb-2.5">
                <button
                    type="button"
                    onClick={() => onChange(participantIds)}
                    className={`participant-quick-action ${allSelected ? 'is-active' : ''}`}
                    aria-pressed={allSelected}
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faUsers} className="text-[0.7rem]" aria-hidden="true" />
                    {t('shared.participantPicker.everyone')}
                </button>
                {canPickOnlyMe && (
                    <button
                        type="button"
                        onClick={() => onChange([currentUser])}
                        className={`participant-quick-action ${onlyMeSelected ? 'is-active' : ''}`}
                        aria-pressed={onlyMeSelected}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faUser} className="text-[0.7rem]" aria-hidden="true" />
                        {t('shared.participantPicker.onlyMe')}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => onChange([])}
                    className="participant-quick-action"
                    disabled={disabled || selected.length === 0}
                >
                    <FontAwesomeIcon icon={faXmark} className="text-[0.7rem]" aria-hidden="true" />
                    {t('shared.participantPicker.clear')}
                </button>
            </div>

            {/* The chips wrap as far down as the roster needs and let the submit
                button ride below the fold. A capped, self-scrolling box was worse
                everywhere it rendered: every parent this picker sits in already
                scrolls — the mobile sheet body, the edit modal body, the sticky
                desktop column — so the cap only nested a second scroll inside the
                first, and on a phone a thumb landing on the chips moved the wrong
                one. Trading the always-visible button for four more rows of names
                is the right way round: picking who shared the bill is the work,
                reaching the button is one scroll at the end of it. */}
            <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label={ariaLabel}
            >
                {participantIds.map(participantId => {
                    const name = participants[participantId];
                    const isSelected = selected.includes(participantId);
                    const ratio = getShareRatio(shareRatios, participantId);

                    return (
                        <button
                            key={participantId}
                            type="button"
                            onClick={() => toggle(participantId)}
                            aria-pressed={isSelected}
                            disabled={disabled}
                            className={`participant-chip ${isSelected ? 'participant-chip-selected' : ''}`}
                        >
                            <span className="truncate max-w-[10rem]">{name}</span>
                            {ratio !== DEFAULT_SHARE_RATIO && (
                                <span
                                    className="-ml-1 text-[0.7rem] font-semibold tabular-nums opacity-80"
                                    title={t('shared.participantPicker.shareRatio', { ratio })}
                                >
                                    {ratio}%
                                </span>
                            )}
                            <span className="participant-chip-mark" aria-hidden="true">
                                {isSelected && <FontAwesomeIcon icon={faCheck} className="text-[0.6rem]" />}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default ParticipantPicker;
